import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Search, Share2, FileJson, FileText as FileTextIcon } from "lucide-react";
import { listEvidence, exportEvidence, exportEvidenceBulk } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import EvidenceTimeline from "@/components/EvidenceTimeline";
import ContributorClassTag from "@/components/ContributorClassTag";
import { toast } from "sonner";

const completenessTone = (c) => {
  if (c >= 0.95) return "bg-emerald-600";
  if (c >= 0.7) return "bg-amber-500";
  return "bg-rose-500";
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function EvidenceConsole() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const artifactFilter = searchParams.get("artifact") || undefined;
    listEvidence(q || undefined, artifactFilter)
      .then((r) => {
        const loaded = r.items || [];
        setItems(loaded);
        setSelected((prev) => prev || loaded[0] || null);
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load evidence records"));
  }, [q, searchParams]);

  async function handleExportSingle(format) {
    if (!selected) return;
    try {
      const blob = await exportEvidence(selected.id, format);
      downloadBlob(blob, `${selected.id}.${format === "pdf" ? "pdf" : "json"}`);
    } catch {
      toast.error("Export failed");
    }
  }

  async function handleExportBulk(format) {
    try {
      const blob = await exportEvidenceBulk(format);
      downloadBlob(blob, "evidence-bundle.zip");
    } catch {
      toast.error("Export failed");
    }
  }

  function handleShareLink() {
    if (!selected) return;
    const url = `${window.location.origin}${window.location.pathname}?ev=${selected.id}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copied"),
      () => toast.error("Could not copy link"),
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-65px)]" data-testid="evidence-console-page">
      <div className="flex items-center justify-between px-8 py-5 border-b border-border/80">
        <div>
          <div className="tracked-caps">Audit-ready</div>
          <h2 className="mt-1 font-serif-display text-3xl">Evidence Console</h2>
          <p className="text-sm text-muted-foreground mt-1">
            The chain-of-custody for every artifact that moved through the governed workflow.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleShareLink}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm hover:bg-muted"
            data-testid="evidence-share"
          >
            <Share2 className="h-3.5 w-3.5" /> Share audit package
          </button>
          <button
            onClick={() => handleExportBulk("json")}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm hover:bg-muted"
            data-testid="evidence-export-json"
          >
            <FileJson className="h-3.5 w-3.5" /> JSON
          </button>
          <button
            onClick={() => handleExportBulk("pdf")}
            className="inline-flex items-center gap-2 rounded-md bg-brand text-brand-foreground px-3 py-2 text-sm hover:opacity-90"
            data-testid="evidence-export-pdf"
          >
            <Download className="h-3.5 w-3.5" /> Export PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 flex-1 min-h-0">
        <aside className="col-span-4 border-r border-border/80 flex flex-col">
          <div className="px-5 py-3 border-b border-border/80 flex items-center gap-2 rounded-none bg-card">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search EV-ID or artifact title"
              className="flex-1 bg-transparent text-sm outline-none"
              data-testid="evidence-search"
            />
          </div>
          <div className="overflow-y-auto">
            {items.map((r) => {
              const active = selected?.id === r.id;
              return (
                <button
                  key={r.id}
                  onClick={() => setSelected(r)}
                  data-testid={`evidence-row-${r.id}`}
                  className={`w-full text-left px-5 py-4 border-b border-border/80 transition-colors ${active ? "bg-muted" : "hover:bg-muted/50"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-muted-foreground">{r.id}</span>
                    <StatusBadge value={r.revocation_status} />
                  </div>
                  <div className="mt-1 text-sm line-clamp-2">{r.artifact_title}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="tracked-caps">{r.source_system}</span>
                    <span className="text-muted-foreground/60">·</span>
                    <span className="font-mono text-[11px] text-muted-foreground">{(r.source_ids || []).length} source ids</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full ${completenessTone(r.completeness)}`} style={{ width: `${(r.completeness || 0) * 100}%` }} />
                    </div>
                    <span className="font-mono text-[11px] text-muted-foreground w-10 text-right">{Math.round((r.completeness || 0) * 100)}%</span>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="col-span-8 overflow-y-auto">
          {!selected ? (
            <div className="p-12 flex flex-col items-center gap-6 text-center">
              <p className="text-muted-foreground">Select an evidence record to view its chain of custody.</p>
              <div className="w-full max-w-md">
                <AccessCTA />
              </div>
            </div>
          ) : (
            <div className="p-8 space-y-8">
              <header>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground">{selected.id}</span>
                  <StatusBadge value={selected.revocation_status} />
                  {selected.completeness < 1 && (
                    <span className="inline-flex items-center gap-1.5 rounded border border-amber-200 bg-amber-50 text-amber-900 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider">
                      incomplete evidence
                    </span>
                  )}
                </div>
                <h3 className="mt-2 font-serif-display text-3xl leading-tight">{selected.artifact_title}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Publication target — {selected.publication_target}
                </p>
              </header>

              <section className="grid md:grid-cols-2 gap-6">
                <div className="rounded-lg border border-border bg-card p-5 space-y-3">
                  <div className="tracked-caps">Record</div>
                  <dl className="space-y-2.5 text-sm">
                    <Row k="Source system">{selected.source_system}</Row>
                    <Row k="Source IDs"><span className="font-mono text-xs">{(selected.source_ids || []).join(", ")}</span></Row>
                    <Row k="Contributor classes">
                      <div className="flex gap-1.5 justify-end">
                        {(selected.contributor_classes || []).map((c) => <ContributorClassTag key={c} value={c} />)}
                      </div>
                    </Row>
                    <Row k="Policy basis">{selected.policy_basis}</Row>
                    <Row k="Review chain">{(selected.review_chain || []).join(" → ")}</Row>
                    <Row k="Approvals"><span className="font-mono text-xs">{selected.approvals}</span></Row>
                  </dl>
                </div>
                <div className="rounded-lg border border-border bg-card p-5 space-y-3">
                  <div className="tracked-caps">Chain of custody</div>
                  <EvidenceTimeline timestamps={selected.timestamps} />
                </div>
              </section>

              <section className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center justify-between">
                  <div className="tracked-caps">Export package</div>
                  <span className="font-mono text-[11px] text-muted-foreground">v1 · auditable</span>
                </div>
                <div className="mt-4 grid md:grid-cols-3 gap-3">
                  <button
                    onClick={() => handleExportSingle("pdf")}
                    className="rounded-md border border-border p-4 text-left hover:bg-muted transition-colors"
                  >
                    <FileTextIcon className="h-4 w-4 text-muted-foreground" />
                    <div className="mt-2 text-sm font-medium">PDF</div>
                    <div className="text-xs text-muted-foreground">Human-readable audit report</div>
                  </button>
                  <button
                    onClick={() => handleExportSingle("json")}
                    className="rounded-md border border-border p-4 text-left hover:bg-muted transition-colors"
                  >
                    <FileJson className="h-4 w-4 text-muted-foreground" />
                    <div className="mt-2 text-sm font-medium">JSON</div>
                    <div className="text-xs text-muted-foreground">Machine-readable evidence bundle</div>
                  </button>
                  <button
                    onClick={handleShareLink}
                    className="rounded-md border border-border p-4 text-left hover:bg-muted transition-colors"
                  >
                    <Share2 className="h-4 w-4 text-muted-foreground" />
                    <div className="mt-2 text-sm font-medium">Share link</div>
                    <div className="text-xs text-muted-foreground">Time-boxed audit package URL</div>
                  </button>
                </div>
              </section>

              {selected.completeness < 1 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
                  <div className="tracked-caps text-amber-900 shrink-0">Missing evidence</div>
                  <div className="text-sm text-amber-900/90">
                    This record is awaiting compliance sign-off. Export is still permitted but will be watermarked as provisional.
                  </div>
                </div>
              )}

              <AccessCTA />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function AccessCTA() {
  return (
    <div className="border border-brand/30 rounded-lg p-5 bg-brand/5">
      <p className="text-sm font-medium mb-1">This is your compliance audit trail.</p>
      <p className="text-sm text-muted-foreground mb-3">
        Every extraction, review, approval, and revocation — with source lineage, policy basis, and reviewer decisions — exportable for legal and internal audit.
      </p>
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 rounded-md bg-brand text-brand-foreground px-3.5 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
      >
        Request access for your org
      </Link>
    </div>
  );
}

function Row({ k, children }) {
  return (
    <div className="flex items-baseline justify-between gap-4 hairline-b pb-2 last:border-0 last:pb-0">
      <dt className="tracked-caps">{k}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}
